import { AUDIENCE_LABELS, MESSAGE_LIMITS, type AudienceGroup } from '@barso/shared';
import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  bazaarApi,
  messagingApi,
  shiftApi,
  type BroadcastPreview,
  type BroadcastShiftSelection,
  type HelperShift,
  type MessageItem,
  type MessageParticipant,
  type MessageRecipient,
  type TeamThreadDetail,
} from '../api/endpoints';
import { messageFor } from '../api/messages';
import { MailboxShell, NewMessageLink } from '../components/Mailbox';
import { MessageList, ReplyForm } from '../components/MessageThreadView';
import { Alert, Button, Card, Checkbox, PageHeader, TextField } from '../components/ui';
import { dateTime } from '../format';
import { AREAS } from '../components/areas';
import { notifyUnreadChanged } from '../hooks/useUnreadMessages';

const SEARCH_RESULTS_MAX = 20;
const PREVIEW_DELAY_MS = 400;

function personName(person: MessageParticipant): string {
  const name =
    person.lastName === null ? person.firstName : `${person.firstName} ${person.lastName}`;
  return person.sellerNumber === null ? name : `${name} (Nr. ${String(person.sellerNumber)})`;
}

function audienceText(groups: readonly AudienceGroup[]): string {
  return groups.map((group) => AUDIENCE_LABELS[group]).join(', ');
}

/** Basar-Postfach: dasselbe Postfach wie unter „Nachrichten“, mit festem Basar (der Lesestand gilt je Teammitglied). */
export function TeamInboxPage() {
  const { eventId = '' } = useParams();
  return (
    <MailboxShell
      eventId={eventId}
      header={
        <PageHeader
          back={{ to: `/basare/${eventId}`, label: 'Basar' }}
          icon={AREAS.nachrichten.icon}
          title={AREAS.nachrichten.title}
          actions={<NewMessageLink to={`/basare/${eventId}/nachrichten/neu`} />}
        />
      }
    />
  );
}

/** Zeilen der Personensuche (Name, Verkäufernummer, Gruppen), höchstens die ersten Treffer. */
function useRecipientSearch(recipients: MessageRecipient[] | null, query: string) {
  return useMemo(() => {
    if (recipients === null) return [];
    const needle = query.trim().toLocaleLowerCase('de-DE');
    const matches =
      needle === ''
        ? recipients
        : recipients.filter((person) =>
            `${personName(person)} ${audienceText(person.groups)}`
              .toLocaleLowerCase('de-DE')
              .includes(needle),
          );
    return matches.slice(0, SEARCH_RESULTS_MAX);
  }, [recipients, query]);
}

/**
 * Formular für eine Nachricht des Teams: Einzelnachricht an eine Person oder (`group`) Rundschreiben an Zielgruppen.
 * Lädt die Empfänger des Basars. `threadBase` ist der Pfad, unter dem sich der neue Faden danach öffnet.
 */
export function TeamComposeForm({
  eventId,
  group,
  threadBase,
  initialSubject = '',
  textHeading,
}: {
  eventId: string;
  group: boolean;
  threadBase: string;
  initialSubject?: string;
  /** Überschrift über Betreff und Text (Schritt 3 im Blatt „Neue Nachricht“). */
  textHeading?: string;
}) {
  const [recipients, setRecipients] = useState<MessageRecipient[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const guard = { cancelled: false };
    messagingApi.recipients(eventId).then(
      (loaded) => {
        if (!guard.cancelled) setRecipients(loaded);
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId]);

  return (
    <>
      {error !== null && <Alert kind="error">{error}</Alert>}
      {recipients === null ? (
        error === null && <p role="status">Einen Moment bitte …</p>
      ) : group ? (
        <BroadcastForm
          eventId={eventId}
          recipients={recipients}
          threadBase={threadBase}
          initialSubject={initialSubject}
          textHeading={textHeading}
        />
      ) : (
        <PersonForm
          eventId={eventId}
          recipients={recipients}
          threadBase={threadBase}
          initialSubject={initialSubject}
          textHeading={textHeading}
        />
      )}
    </>
  );
}

/** Verfassen im Basar-Postfach: Einzelnachricht an eine Person oder (`?art=rundschreiben`) Rundschreiben. */
export function TeamComposePage() {
  const { eventId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [mayBroadcast, setMayBroadcast] = useState<boolean | null>(null);
  // „Gruppe“ nur mit dem Recht für Rundschreiben (der Server prüft weiter); solange unbekannt, gilt die Adresse
  const broadcast = params.get('art') === 'rundschreiben' && mayBroadcast !== false;

  useEffect(() => {
    const guard = { cancelled: false };
    bazaarApi.getEvent(eventId).then(
      (loaded) => {
        if (!guard.cancelled) {
          setMayBroadcast((loaded.myPermissions ?? []).includes('event.messages.broadcast'));
        }
      },
      () => undefined,
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId]);

  return (
    <div className="stack stack--lg">
      <PageHeader
        back={{ to: `/basare/${eventId}/nachrichten`, label: 'Nachrichten' }}
        title="Neue Nachricht"
      />
      {mayBroadcast === true && (
        <div className="chips" role="group" aria-label="Art des Empfängers">
          <button
            type="button"
            className="chip"
            aria-pressed={!broadcast}
            onClick={() => {
              setParams({}, { replace: true });
            }}
          >
            An eine Person
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={broadcast}
            onClick={() => {
              setParams({ art: 'rundschreiben' }, { replace: true });
            }}
          >
            An eine Gruppe
          </button>
        </div>
      )}
      <TeamComposeForm
        eventId={eventId}
        group={broadcast}
        threadBase={`/basare/${eventId}/nachrichten`}
      />
    </div>
  );
}

export function SubjectAndText({
  subject,
  text,
  onSubject,
  onText,
  heading,
}: {
  subject: string;
  text: string;
  onSubject: (value: string) => void;
  onText: (value: string) => void;
  heading?: string | undefined;
}) {
  return (
    <>
      {heading !== undefined && <h2>{heading}</h2>}
      <TextField
        label="Betreff"
        required
        maxLength={MESSAGE_LIMITS.subjectMax}
        value={subject}
        onChange={(event) => {
          onSubject(event.target.value);
        }}
      />
      <div className="field">
        <label htmlFor="team-nachricht-text">Nachricht</label>
        <textarea
          id="team-nachricht-text"
          rows={8}
          required
          maxLength={MESSAGE_LIMITS.textMax}
          value={text}
          onChange={(event) => {
            onText(event.target.value);
          }}
        />
        <span className="field__hint">
          {text.length} von {MESSAGE_LIMITS.textMax} Zeichen. Nur Text, keine Anhänge.
        </span>
      </div>
    </>
  );
}

function PersonForm({
  eventId,
  recipients,
  threadBase,
  initialSubject,
  textHeading,
}: {
  eventId: string;
  recipients: MessageRecipient[];
  threadBase: string;
  initialSubject: string;
  textHeading: string | undefined;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [subject, setSubject] = useState(initialSubject);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hits = useRecipientSearch(recipients, query);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (selected === null) return;
    setBusy(true);
    setError(null);
    messagingApi.startTeamThread(eventId, { recipientUserId: selected, subject, text }).then(
      (thread) => {
        notifyUnreadChanged();
        void navigate(`${threadBase}/${thread.id}`);
      },
      (caught: unknown) => {
        setError(messageFor(caught));
        setBusy(false);
      },
    );
  };

  return (
    <Card>
      <form className="stack" onSubmit={submit}>
        {error !== null && <Alert kind="error">{error}</Alert>}
        <fieldset className="stack">
          <legend>Empfänger</legend>
          <TextField
            label="Person suchen"
            hint="Name, Verkäufernummer oder Gruppe"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          {hits.length === 0 ? (
            <p className="muted">Keine Person gefunden.</p>
          ) : (
            hits.map((person) => (
              <div className="check" key={person.userId}>
                <input
                  type="radio"
                  name="empfaenger"
                  id={`empfaenger-${person.userId}`}
                  checked={selected === person.userId}
                  onChange={() => {
                    setSelected(person.userId);
                  }}
                />
                <label htmlFor={`empfaenger-${person.userId}`}>
                  {personName(person)}{' '}
                  <span className="muted">· {audienceText(person.groups)}</span>
                </label>
              </div>
            ))
          )}
        </fieldset>
        <SubjectAndText
          subject={subject}
          text={text}
          onSubject={setSubject}
          onText={setText}
          heading={textHeading}
        />
        <div>
          <Button
            icon="mail"
            type="submit"
            busy={busy}
            disabled={selected === null || subject.trim() === '' || text.trim() === ''}
          >
            Nachricht senden
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Ab so vielen Empfängern fragt die Oberfläche vor dem Senden nach. */
const CONFIRM_FROM = 100;

/** Gruppen der drei Blöcke im Formular (Plan Nr. 11). */
const TEAM_GROUPS: readonly AudienceGroup[] = [
  'managers',
  'cashiers',
  'intake',
  'helpers',
  'shift_signups',
];
const SELLER_GROUPS: readonly AudienceGroup[] = ['sellers', 'applicants'];

interface ShiftChoice {
  includeWaitlist: boolean;
  includeExternal: boolean;
}

function shiftTitle(shift: HelperShift): string {
  return shift.startsAt === null ? shift.name : `${shift.name} · ${dateTime(shift.startsAt)}`;
}

function BroadcastForm({
  eventId,
  recipients,
  threadBase,
  initialSubject,
  textHeading,
}: {
  eventId: string;
  recipients: MessageRecipient[];
  threadBase: string;
  initialSubject: string;
  textHeading: string | undefined;
}) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<AudienceGroup[]>([]);
  const [shifts, setShifts] = useState<HelperShift[]>([]);
  const [choices, setChoices] = useState<Record<string, ShiftChoice>>({});
  const [persons, setPersons] = useState<MessageRecipient[]>([]);
  const [query, setQuery] = useState('');
  const [subject, setSubject] = useState(initialSubject);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ key: string; result: BroadcastPreview } | null>(null);
  const [previewError, setPreviewError] = useState<{ key: string; message: string } | null>(null);
  const hits = useRecipientSearch(recipients, query).filter(
    (person) => !persons.some((chosen) => chosen.userId === person.userId),
  );

  // Schichten für die Auswahl; ohne Recht oder bei Fehler fehlt der Block einfach (die Gruppen bleiben nutzbar)
  useEffect(() => {
    const guard = { cancelled: false };
    shiftApi.list(eventId).then(
      (loaded) => {
        if (!guard.cancelled) setShifts(loaded);
      },
      () => undefined,
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId]);

  const userIds = persons.map((person) => person.userId);
  const shiftTargets: BroadcastShiftSelection[] = Object.entries(choices)
    .map(([shiftId, choice]) => ({ shiftId, ...choice }))
    .sort((a, b) => a.shiftId.localeCompare(b.shiftId));
  const hasTarget = groups.length > 0 || userIds.length > 0 || shiftTargets.length > 0;
  // Auswahl als ein Schlüssel: Die Vorschau gilt nur für genau diese Auswahl
  const key = JSON.stringify([[...groups].sort(), [...userIds].sort(), shiftTargets]);

  // Empfängerzahl live, entprellt: zählt der Server (Vereinigung, ohne Doppelte, ohne Absender)
  useEffect(() => {
    const [audiences, ids, selected] = JSON.parse(key) as [
      AudienceGroup[],
      string[],
      BroadcastShiftSelection[],
    ];
    if (audiences.length === 0 && ids.length === 0 && selected.length === 0) return;
    const guard = { cancelled: false };
    const timer = window.setTimeout(() => {
      messagingApi.previewBroadcast(eventId, { audiences, userIds: ids, shifts: selected }).then(
        (result) => {
          if (guard.cancelled) return;
          setPreview({ key, result });
          setPreviewError(null);
        },
        (caught: unknown) => {
          if (!guard.cancelled) setPreviewError({ key, message: messageFor(caught) });
        },
      );
    }, PREVIEW_DELAY_MS);
    return () => {
      guard.cancelled = true;
      window.clearTimeout(timer);
    };
  }, [eventId, key]);

  const current = preview?.key === key ? preview.result : null;
  const count = current?.recipients ?? null;
  const countError = previewError?.key === key ? previewError.message : null;
  const anyExternal = shiftTargets.some((item) => item.includeExternal);

  const setMany = (list: readonly AudienceGroup[], on: boolean): void => {
    setGroups((existing) => {
      const rest = existing.filter((entry) => !list.includes(entry));
      return on ? [...rest, ...list] : rest;
    });
  };
  const has = (...list: AudienceGroup[]): boolean => list.every((entry) => groups.includes(entry));
  const setChoice = (shiftId: string, change: Partial<ShiftChoice> | null): void => {
    setChoices((existing) => {
      if (change === null) {
        return Object.fromEntries(Object.entries(existing).filter(([id]) => id !== shiftId));
      }
      const base = existing[shiftId] ?? { includeWaitlist: false, includeExternal: false };
      return { ...existing, [shiftId]: { ...base, ...change } };
    });
  };

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (count === null) return;
    if (count >= CONFIRM_FROM && !window.confirm(`An ${String(count)} Personen senden?`)) return;
    setBusy(true);
    setError(null);
    messagingApi
      .sendBroadcast(eventId, {
        audiences: groups,
        userIds,
        shifts: shiftTargets,
        subject,
        text,
      })
      .then(
        (result) => {
          notifyUnreadChanged();
          void navigate(`${threadBase}/${result.threadId}`);
        },
        (caught: unknown) => {
          setError(messageFor(caught));
          setBusy(false);
        },
      );
  };

  const groupBox = (group: AudienceGroup) => (
    <Checkbox
      key={group}
      label={AUDIENCE_LABELS[group]}
      checked={groups.includes(group)}
      onChange={(event) => {
        setMany([group], event.target.checked);
      }}
    />
  );

  return (
    <Card>
      <form className="stack" onSubmit={submit}>
        {error !== null && <Alert kind="error">{error}</Alert>}
        <fieldset className="stack">
          <legend>Team</legend>
          <Checkbox
            label="Alle Helfer"
            checked={has('helpers', 'shift_signups')}
            onChange={(event) => {
              setMany(['helpers', 'shift_signups'], event.target.checked);
            }}
          />
          {TEAM_GROUPS.map(groupBox)}
        </fieldset>
        {shifts.length > 0 && (
          <fieldset className="stack">
            <legend>Schichten</legend>
            {shifts.map((shift) => {
              const choice = choices[shift.id];
              return (
                <div className="stack" key={shift.id} style={{ gap: 'var(--space-xs)' }}>
                  <Checkbox
                    label={`Schicht ${shiftTitle(shift)}`}
                    checked={choice !== undefined}
                    onChange={(event) => {
                      setChoice(shift.id, event.target.checked ? {} : null);
                    }}
                  />
                  {choice !== undefined && (
                    <div className="stack" style={{ marginLeft: 'var(--space-lg)' }}>
                      <Checkbox
                        label={`Wartende einschließen (${shift.name})`}
                        checked={choice.includeWaitlist}
                        onChange={(event) => {
                          setChoice(shift.id, { includeWaitlist: event.target.checked });
                        }}
                      />
                      <Checkbox
                        label={`Externe per E-Mail (${shift.name})`}
                        checked={choice.includeExternal}
                        onChange={(event) => {
                          setChoice(shift.id, { includeExternal: event.target.checked });
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {anyExternal && (
              <Alert kind="info">
                Externe ohne Konto können sich nicht anmelden. Sie bekommen den Inhalt per E-Mail,
                ohne Antwortmöglichkeit. Das ist die einzige E-Mail mit Inhalt, sie enthält einen
                Link zum Abbestellen.
              </Alert>
            )}
          </fieldset>
        )}
        <fieldset className="stack">
          <legend>Verkäufer</legend>
          <Checkbox
            label="Alle Verkäufer"
            checked={has('sellers', 'applicants')}
            onChange={(event) => {
              setMany(SELLER_GROUPS, event.target.checked);
            }}
          />
          {SELLER_GROUPS.map(groupBox)}
        </fieldset>
        <fieldset className="stack">
          <legend>Einzelne Personen (optional)</legend>
          {persons.length > 0 && (
            <ul className="list" aria-label="Ausgewählte Personen">
              {persons.map((person) => (
                <li key={person.userId}>
                  <span>{personName(person)}</span>
                  <Button
                    icon="trash"
                    compactOnMobile
                    variant="secondary"
                    small
                    aria-label={`${personName(person)} entfernen`}
                    onClick={() => {
                      setPersons((existing) =>
                        existing.filter((entry) => entry.userId !== person.userId),
                      );
                    }}
                  >
                    Entfernen
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {persons.length >= MESSAGE_LIMITS.broadcastUserIdsMax ? (
            <p className="muted">
              Höchstens {MESSAGE_LIMITS.broadcastUserIdsMax} einzelne Personen. Nutzen Sie
              Zielgruppen für größere Kreise.
            </p>
          ) : (
            <>
              <TextField
                label="Person suchen"
                hint="Name, Verkäufernummer oder Gruppe"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              {query.trim() !== '' && (
                <ul className="list" aria-label="Suchergebnisse">
                  {hits.map((person) => (
                    <li key={person.userId}>
                      <span>
                        {personName(person)}{' '}
                        <span className="muted">· {audienceText(person.groups)}</span>
                      </span>
                      <Button
                        icon="plus"
                        compactOnMobile
                        variant="secondary"
                        small
                        aria-label={`${personName(person)} hinzufügen`}
                        onClick={() => {
                          setPersons((existing) => [...existing, person]);
                        }}
                      >
                        Hinzufügen
                      </Button>
                    </li>
                  ))}
                  {hits.length === 0 && <li className="muted">Keine Person gefunden.</li>}
                </ul>
              )}
            </>
          )}
        </fieldset>
        <div aria-live="polite" className="stack">
          {!hasTarget ? (
            <p className="muted">Wählen Sie mindestens eine Zielgruppe, Schicht oder Person.</p>
          ) : countError !== null ? (
            <Alert kind="error">{countError}</Alert>
          ) : current === null ? (
            <p className="muted">Empfänger werden gezählt …</p>
          ) : (
            <>
              <p>
                Geht an <strong>{current.recipients}</strong>{' '}
                {current.recipients === 1 ? 'Person' : 'Personen'}
                {current.externals > 0
                  ? ` (${String(current.accounts)} im Posteingang, ${String(current.externals)} nur per E-Mail)`
                  : ''}
                . Der Kreis wird beim Senden festgeschrieben. Auf Rundschreiben kann man nicht
                antworten.
              </p>
              {current.unconfirmedExternals > 0 && (
                <p className="muted">
                  {current.unconfirmedExternals}{' '}
                  {current.unconfirmedExternals === 1 ? 'Adresse' : 'Adressen'} ohne bestätigte
                  Adresse (Eintragung ohne Konto){' '}
                  {current.unconfirmedExternals === 1 ? 'wird' : 'werden'} übersprungen. Die Person
                  bekommt die Nachricht per E-Mail erst, wenn sie ihre Adresse über den Link in der
                  Eintragungs-Mail bestätigt hat.
                </p>
              )}
              {current.groups.length > 0 && (
                <ul className="list" aria-label="Aufschlüsselung nach Gruppe">
                  {current.groups.map((entry) => (
                    <li key={entry.key}>
                      <span>{entry.label}</span>
                      <span className="muted">{entry.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <SubjectAndText
          subject={subject}
          text={text}
          onSubject={setSubject}
          onText={setText}
          heading={textHeading}
        />
        <div>
          <Button
            icon="mail"
            type="submit"
            busy={busy}
            disabled={count === null || count === 0 || subject.trim() === '' || text.trim() === ''}
          >
            Rundschreiben senden
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Verlauf eines Gesprächs oder Rundschreibens aus Sicht des Teams. Beim Öffnen gilt der Faden für Sie als gelesen.
 * `fromMailbox`: aus dem Postfach unter `/nachrichten` geöffnet, „Zurück“ führt dorthin.
 */
export function TeamThreadPage({ fromMailbox = false }: { fromMailbox?: boolean }) {
  const { eventId = '', threadId = '' } = useParams();
  const [thread, setThread] = useState<TeamThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const guard = { cancelled: false };
    messagingApi.teamThread(eventId, threadId).then(
      (loaded) => {
        if (guard.cancelled) return;
        setThread(loaded);
        if (loaded.unread) {
          messagingApi.markTeamRead(eventId, threadId).then(notifyUnreadChanged, () => undefined);
        }
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId, threadId]);

  const back = {
    to: fromMailbox ? '/nachrichten' : `/basare/${eventId}/nachrichten`,
    label: 'Nachrichten',
  };
  if (thread === null) {
    return error === null ? (
      <p role="status">Einen Moment bitte …</p>
    ) : (
      <div className="stack">
        <PageHeader back={back} title="Nachricht" />
        <Alert kind="error">{error}</Alert>
      </div>
    );
  }

  const loadOlder = (): void => {
    const first = thread.messages[0];
    if (first === undefined) return;
    setBusy(true);
    messagingApi.teamThread(eventId, threadId, first.id).then(
      (older) => {
        setThread((current) =>
          current === null
            ? current
            : {
                ...current,
                messages: [...older.messages, ...current.messages],
                hasMore: older.hasMore,
              },
        );
        setBusy(false);
      },
      (caught: unknown) => {
        setError(messageFor(caught));
        setBusy(false);
      },
    );
  };

  const reply = async (replyText: string): Promise<void> => {
    const sent: MessageItem = await messagingApi.replyTeam(eventId, threadId, replyText);
    setThread((current) =>
      current === null
        ? current
        : { ...current, messages: [...current.messages, sent], unread: false },
    );
    notifyUnreadChanged();
  };

  const isBroadcast = thread.kind === 'broadcast';
  return (
    <div className="stack stack--lg">
      <PageHeader
        back={back}
        title={thread.subject}
        meta={
          isBroadcast ? (
            <>
              Rundschreiben{' '}
              {thread.audienceLabel ??
                `an ${audienceText(thread.audiences) || 'ausgewählte Personen'}`}{' '}
              · {thread.recipientCount} Empfänger
              {thread.externalCount > 0 &&
                ` und ${String(thread.externalCount)} per E-Mail an Externe`}
              {thread.readCount !== null &&
                ` · gelesen ${String(thread.readCount)} von ${String(thread.recipientCount)}`}
            </>
          ) : thread.participant === null ? undefined : (
            <>
              {personName(thread.participant)} · Gespräch, gesendet {dateTime(thread.createdAt)}
            </>
          )
        }
      />
      {error !== null && <Alert kind="error">{error}</Alert>}
      {thread.hasMore && (
        <div>
          <Button icon="chevron-down" variant="secondary" busy={busy} onClick={loadOlder}>
            Ältere Nachrichten laden
          </Button>
        </div>
      )}
      <MessageList messages={thread.messages} />
      {isBroadcast ? (
        <Alert kind="info">
          Auf Rundschreiben kann man nicht antworten. Fragen kommen als eigene Gespräche bei Ihnen
          an.
        </Alert>
      ) : (
        <Card>
          <ReplyForm maxLength={MESSAGE_LIMITS.textMax} onSend={reply} />
        </Card>
      )}
    </div>
  );
}
